import {
  type ElasticFilter,
  isDefined,
  type ElasticFiltersInput,
  type ElasticFiltersSuccess,
  Logger,
  ElasticFilterSchema,
  isNotEqual,
} from '@terros/common'
import { type QueryKey, type QueryClient } from '@tanstack/react-query'
import { searchAccountsKey } from './keys'

const logger = new Logger('findCachedDataForRegion')
logger.level = 'verbose'

export const queryObjectToKey = (input: ElasticFiltersInput): QueryKey => {
  return [...searchAccountsKey(), input.assignmentMode, input.filters, input.fields]
}
export const findCachedDataForRegion = (
  queryClient: QueryClient,
  input: ElasticFiltersInput
): ElasticFiltersSuccess | undefined => {
  if (!input.filters?.boundingBox) return
  const validQueries = toValidQueries(queryClient, input).sort((a, b) => (regionContains(b.bbox, a.bbox) ? 1 : -1))
  const validQuery = validQueries.at(0)
  if (!validQuery) return
  return validQuery.data
}

type ValidQuery = {
  bbox: NonNullable<ElasticFilter['boundingBox']>
  data: ElasticFiltersSuccess
}

const toValidQueries = (queryClient: QueryClient, input: ElasticFiltersInput): ValidQuery[] => {
  const cachedQueries = queryClient.getQueriesData<ElasticFiltersSuccess>({
    queryKey: [...searchAccountsKey(), input.assignmentMode],
    stale: false,
  })
  const { boundingBox: inputBoundingBox, ...inputRest } = input.filters ?? {}
  if (!inputBoundingBox) return []

  return cachedQueries
    .map(([key, data]) => {
      if (!data) return
      if (data.mode !== 'account') return
      if (isNotEqual(key[3], input.fields)) return
      const keyInput = key[2]
      const parsedKey = ElasticFilterSchema.safeParse(keyInput)
      if (!parsedKey.success) return
      const parsedInput = parsedKey.data
      const { boundingBox: parsedBoundingBox, ...rest } = parsedInput ?? {}
      if (!parsedBoundingBox) return
      if (isNotEqual(inputRest, rest)) return
      if (!regionContains(parsedBoundingBox, inputBoundingBox)) return
      return {
        bbox: parsedBoundingBox,
        data,
      }
    })
    .filter(isDefined)
}

type FilterBbox = NonNullable<ElasticFilter['boundingBox']>

// check if regionA contains regionB
const regionContains = (regionA: FilterBbox, regionB: FilterBbox): boolean => {
  return (
    regionA.top.latitude >= regionB.top.latitude &&
    regionA.top.longitude <= regionB.top.longitude &&
    regionA.bottom.latitude <= regionB.bottom.latitude &&
    regionA.bottom.longitude >= regionB.bottom.longitude
  )
}
